import React, { useRef, useEffect, Fragment } from 'react';

// locals
import UmlConnectorPath from './UmlConnectorPath.jsx';
import UmlConnectorWaypoints from './UmlConnectorWaypoints.jsx';
import UmlConnectorEndpoints from './UmlConnectorEndpoints.jsx';
import { ConnectorBuilder } from '@jxUml';
import './UmlConnectorLayer.css';

// Single SVG overlay for all connectors: start (edge of from-shape) -> waypoints -> end (edge of to-shape).
// connector.label.x/y is an offset relative to the line's midpoint, not an absolute canvas coordinate.

function UmlConnectorLayer({
    connectors,
    getShapeById,
    onPointMoved,
    onLabelMoved,
    onDerivedLabelMoved,
    onConnectorDoubleClick,
    onPointRemove,
    onAnchorDragStart,
    onConnectorDelete,
    toWorldPoint,
}) {
    const hoveredConnectorIdRef = useRef(null);

    // Single stable keydown listener for the layer's lifetime; hoveredConnectorIdRef tracks the current hover target.
    useEffect(
        function attachConnectorDeleteListener() {
            function onKeyDown(ev) {
                if (ev.key.toLowerCase() !== 'd') {
                    return;
                }
                if (hoveredConnectorIdRef.current) {
                    onConnectorDelete?.(hoveredConnectorIdRef.current);
                }
            }
            window.addEventListener('keydown', onKeyDown, true);
            return function detachConnectorDeleteListener() {
                window.removeEventListener('keydown', onKeyDown, true);
            };
        },
        [onConnectorDelete],
    );

    // No useMemo: UmlCanvasEngine mutates connector points in place, so reference equality would block redraw after a drag.
    const renderedConnectors = connectors
        .map(function prepareConnector(connector) {
            const fromShape = getShapeById(connector.fromShapeId);
            const toShape = getShapeById(connector.toShapeId);
            if (!fromShape || !toShape) {
                return null;
            }
            const points = ConnectorBuilder.points(connector, fromShape, toShape);
            const midpoint = ConnectorBuilder.midpoint(points);
            return { connector, points, midpoint, style: ConnectorBuilder.style(connector) };
        })
        .filter(Boolean);

    return (
        <Fragment>
            <svg className="uml-connector-layer">
                <defs>
                    <marker
                        id="uml-arrow-open"
                        viewBox="0 0 10 10"
                        refX="8"
                        refY="5"
                        markerWidth="6"
                        markerHeight="6"
                        orient="auto-start-reverse"
                    >
                        <path d="M2 1L8 5L2 9" fill="none" stroke="context-stroke" strokeWidth="1.5" />
                    </marker>
                    <marker
                        id="uml-arrow-triangle"
                        viewBox="0 0 10 10"
                        refX="8"
                        refY="5"
                        markerWidth="8"
                        markerHeight="8"
                        orient="auto-start-reverse"
                    >
                        <path d="M1 1L9 5L1 9Z" fill="#1c2b3f" stroke="context-stroke" strokeWidth="1" />
                    </marker>
                </defs>

                {renderedConnectors.map(function renderConnector({ connector, points, midpoint, style }) {
                    return (
                        <UmlConnectorPath
                            key={connector.id}
                            connector={connector}
                            points={points}
                            midpoint={midpoint}
                            style={style}
                            onConnectorDoubleClick={onConnectorDoubleClick}
                            onLabelMoved={onLabelMoved}
                            onDerivedLabelMoved={onDerivedLabelMoved}
                            hoveredConnectorIdRef={hoveredConnectorIdRef}
                        />
                    );
                })}
            </svg>

            {/* Separate layer, own (higher-than-shapes) z-index (2026-07-12) — anchors AND waypoints
                need to sit ABOVE class/package widgets (.uml-shape-layer/.uml-package-layer) so they're
                grabbable even where they land right on/near a shape's edge; the LINE itself (svg above)
                stays behind shapes on purpose (doesn't cut through captions). Waypoints moved here
                (were previously in the line-svg above, z=10 — ungrabbable when landing on/near a
                shape, same reason anchors already live here). Same renderedConnectors, computed once —
                no duplicate buildPathPoints work. See UmlConnectorLayer.css for the z-index value. */}
            <svg className="uml-connector-anchor-layer">
                {renderedConnectors.map(function renderWaypoints({ connector }) {
                    return (
                        <UmlConnectorWaypoints
                            key={connector.id}
                            connector={connector}
                            onPointMoved={onPointMoved}
                            onPointRemove={onPointRemove}
                            toWorldPoint={toWorldPoint}
                        />
                    );
                })}
                {renderedConnectors.map(function renderEndpoints({ connector, points }) {
                    return (
                        <UmlConnectorEndpoints
                            key={connector.id}
                            connector={connector}
                            points={points}
                            onAnchorDragStart={onAnchorDragStart}
                        />
                    );
                })}
            </svg>
        </Fragment>
    );
}

export default UmlConnectorLayer;
